import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { TicketStatusBadge, type TicketStatus } from './TicketStatusBadge';
import { TicketPriorityBadge, type TicketPriority } from './TicketPriorityBadge';
import { TagBadge } from './TagBadge';
import { cn } from '@/lib/utils';
import { timeAgo, formatFullDate } from '../utils/timeAgo';
import { UserIdentity } from './UserIdentity';
import { ArcheChip } from './ArcheChip';
import type { SupportTagResponse, SupportUserRef, ArcheDisplayMeta } from '@/lib/api';

interface AdminTicket {
  ticketId: string;
  subject: string;
  userId?: string;
  user?: SupportUserRef | null;
  userName?: string;
  unread?: boolean;
  archeId?: string | null;
  status: string;
  priority: string;
  aiPriority?: string | null;
  categoryId: string | null;
  categoryName?: string;
  assignedTo?: string | null;
  assignedToName?: string;
  createdAt: string;
  updatedAt: string;
  tags?: SupportTagResponse[];
}

type SortColumn = 'ai_priority' | 'priority' | 'status' | 'category' | 'created_at';
type SortDirection = 'asc' | 'desc';

interface SortState {
  column: SortColumn | null;
  direction: SortDirection;
}

interface AdminTicketTableProps {
  tickets: AdminTicket[];
  onTicketClick: (ticketId: string) => void;
  selectedTicketIds?: Set<string>;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  onInlineStatusChange?: (ticketId: string, status: string) => Promise<void>;
  sort?: SortState;
  onSortChange?: (sort: SortState) => void;
  /** Resolved arche display meta (name + icon) for tagged tickets. */
  archeMetaById?: Map<string, ArcheDisplayMeta>;
}

const statusOptions: { value: TicketStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'waiting_on_customer', label: 'Waiting' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

function SortIndicator({ column, sort }: { column: SortColumn; sort: SortState }) {
  if (sort.column !== column) return null;
  return sort.direction === 'desc' ? (
    <ArrowDown className="h-3 w-3 inline ml-1" />
  ) : (
    <ArrowUp className="h-3 w-3 inline ml-1" />
  );
}

export function AdminTicketTable({
  tickets,
  onTicketClick,
  selectedTicketIds,
  onSelectionChange,
  onInlineStatusChange,
  sort = { column: null, direction: 'desc' },
  onSortChange,
  archeMetaById,
}: AdminTicketTableProps) {
  const [changingStatus, setChangingStatus] = useState<string | null>(null);
  const selectable = !!onSelectionChange && !!selectedTicketIds;
  const allSelected =
    selectable && tickets.length > 0 && tickets.every((t) => selectedTicketIds.has(t.ticketId));
  const someSelected = selectable && tickets.some((t) => selectedTicketIds.has(t.ticketId));

  const handleSelectAll = () => {
    if (!onSelectionChange) return;
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(tickets.map((t) => t.ticketId)));
    }
  };

  const handleSelectOne = (ticketId: string) => {
    if (!onSelectionChange || !selectedTicketIds) return;
    const next = new Set(selectedTicketIds);
    if (next.has(ticketId)) {
      next.delete(ticketId);
    } else {
      next.add(ticketId);
    }
    onSelectionChange(next);
  };

  const handleStatusChange = async (ticketId: string, newStatus: string, e: Event) => {
    e.stopPropagation();
    if (!onInlineStatusChange) return;
    setChangingStatus(ticketId);
    try {
      await onInlineStatusChange(ticketId, newStatus);
    } finally {
      setChangingStatus(null);
    }
  };

  const handleSortClick = (column: SortColumn) => {
    if (!onSortChange) return;
    if (sort.column === column) {
      if (sort.direction === 'desc') {
        onSortChange({ column, direction: 'asc' });
      } else {
        // Third click: remove sort
        onSortChange({ column: null, direction: 'desc' });
      }
    } else {
      // First click: sort descending
      onSortChange({ column, direction: 'desc' });
    }
  };

  const sortableHeaderClass = onSortChange
    ? 'cursor-pointer select-none hover:text-foreground'
    : '';

  if (tickets.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
        No tickets found.
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {selectable && (
            <TableHead className="w-[40px]">
              <Checkbox
                checked={allSelected}
                onCheckedChange={handleSelectAll}
                aria-label="Select all tickets"
                className={someSelected && !allSelected ? 'opacity-50' : ''}
              />
            </TableHead>
          )}
          <TableHead className="min-w-[250px]">Subject</TableHead>
          <TableHead>User</TableHead>
          <TableHead className={sortableHeaderClass} onClick={() => handleSortClick('status')}>
            Status
            <SortIndicator column="status" sort={sort} />
          </TableHead>
          <TableHead className={sortableHeaderClass} onClick={() => handleSortClick('priority')}>
            Priority
            <SortIndicator column="priority" sort={sort} />
          </TableHead>
          <TableHead className={sortableHeaderClass} onClick={() => handleSortClick('ai_priority')}>
            AI Priority
            <SortIndicator column="ai_priority" sort={sort} />
          </TableHead>
          <TableHead className={sortableHeaderClass} onClick={() => handleSortClick('category')}>
            Category
            <SortIndicator column="category" sort={sort} />
          </TableHead>
          <TableHead className={sortableHeaderClass} onClick={() => handleSortClick('created_at')}>
            Created
            <SortIndicator column="created_at" sort={sort} />
          </TableHead>
          <TableHead>Updated</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {tickets.map((ticket) => {
          const isSelected = selectable && selectedTicketIds.has(ticket.ticketId);
          const isChanging = changingStatus === ticket.ticketId;

          return (
            <TableRow
              key={ticket.ticketId}
              className={`cursor-pointer ${isSelected ? 'bg-muted/50' : ''}`}
              onClick={() => onTicketClick(ticket.ticketId)}
            >
              {selectable && (
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => handleSelectOne(ticket.ticketId)}
                    aria-label={`Select ticket ${ticket.subject}`}
                  />
                </TableCell>
              )}
              <TableCell className="font-medium">
                <div className="space-y-1">
                  <span className="flex items-center gap-1.5">
                    {ticket.unread && (
                      <span
                        className="h-2 w-2 flex-shrink-0 rounded-full bg-primary"
                        aria-label="Unread"
                        title="Unread — new customer activity"
                      />
                    )}
                    <span className={cn('line-clamp-1', ticket.unread && 'font-semibold')}>
                      {ticket.subject}
                    </span>
                  </span>
                  <div className="flex flex-wrap items-center gap-1">
                    <ArcheChip
                      archeId={ticket.archeId}
                      meta={ticket.archeId ? archeMetaById?.get(ticket.archeId) : undefined}
                    />
                    {ticket.tags?.map((tag) => (
                      <TagBadge key={tag.tagId} name={tag.name} color={tag.color} />
                    ))}
                  </div>
                </div>
              </TableCell>
              <TableCell className="max-w-[160px] text-muted-foreground">
                <UserIdentity user={ticket.user} userId={ticket.userId} />
              </TableCell>
              <TableCell onClick={(e) => e.stopPropagation()}>
                {onInlineStatusChange ? (
                  <Select
                    value={ticket.status}
                    onValueChange={(value) => {
                      handleStatusChange(ticket.ticketId, value, new Event('change'));
                    }}
                    disabled={isChanging}
                  >
                    <SelectTrigger className="h-7 w-[130px] text-xs border-none shadow-none hover:bg-muted/50 p-0 pl-1">
                      <SelectValue>
                        <TicketStatusBadge status={ticket.status as TicketStatus} />
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <TicketStatusBadge status={opt.value} />
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <TicketStatusBadge status={ticket.status as TicketStatus} />
                )}
              </TableCell>
              <TableCell>
                <TicketPriorityBadge priority={ticket.priority as TicketPriority} />
              </TableCell>
              <TableCell>
                {ticket.aiPriority ? (
                  <TicketPriorityBadge priority={ticket.aiPriority as TicketPriority} />
                ) : (
                  <span className="text-xs text-muted-foreground">--</span>
                )}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {ticket.categoryName || ticket.categoryId || '--'}
              </TableCell>
              <TableCell
                className="text-muted-foreground whitespace-nowrap"
                title={formatFullDate(ticket.createdAt)}
              >
                {timeAgo(ticket.createdAt)}
              </TableCell>
              <TableCell
                className="text-muted-foreground whitespace-nowrap"
                title={formatFullDate(ticket.updatedAt)}
              >
                {timeAgo(ticket.updatedAt)}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
